import { useState, type ReactNode } from 'react';

interface Props {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  typeToConfirm?: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = '확인',
  cancelLabel = '취소',
  destructive = false,
  typeToConfirm,
  onConfirm,
  onCancel,
}: Props) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const canConfirm = typeToConfirm ? typed === typeToConfirm : true;

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-ink-950/40 p-0 backdrop-blur-[2px] sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cd-title"
    >
      <div className="w-full max-w-md rounded-t-2xl bg-paper p-6 shadow-modal sm:rounded-2xl sm:p-7">
        <h2
          id="cd-title"
          className="text-[20px] font-medium tracking-[-0.01em] text-ink-950"
        >
          {title}
        </h2>
        {description && (
          <div className="mt-3 text-[14px] leading-[1.6] text-ink-600">{description}</div>
        )}

        {typeToConfirm && (
          <div className="mt-5">
            <label className="block text-[13px] text-ink-600">
              계속하려면{' '}
              <span className="rounded-md bg-ink-100 px-1.5 py-0.5 font-mono text-[12px] font-semibold text-ink-900">
                {typeToConfirm}
              </span>{' '}
              를 입력하세요.
            </label>
            <input
              autoFocus
              className="input mt-2"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
            />
          </div>
        )}

        <div className="mt-7 flex justify-end gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={destructive ? 'btn-danger-strong' : 'btn-primary'}
            disabled={!canConfirm || busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm();
              } finally {
                setBusy(false);
                setTyped('');
              }
            }}
          >
            {busy ? '처리 중…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
